/**
 * Iter 359 — Employee profile form (2026-02-27).
 *
 * Dirk: "convert this profile setup for each employee to a proper form
 * field, same as like the credit application … with the progress bar,
 * so we can ensure to capture all details. And then you have to add
 * all legal required details into profile … remuneration section …
 * addresses … medical details … next of kin … everything which we
 * might need from an employee."
 *
 * Design:
 * - Uses the shared `StepIndex` component (same pattern as
 *   AccountApplicationPublic + LeadOnboardingForm).
 * - 9 sections rendered one at a time.
 * - Every section has its own "Save section" button so HR can save
 *   incrementally — no scary "big red submit" at the end.
 * - Login email changes go through the same primary-email swap
 *   endpoint the Users & Roles tool uses (so this page IS the
 *   canonical HR surface; changes here flow back to the Users
 *   section).
 * - Avatar upload lives inside the Identity step.
 *
 * NOTE: We do NOT re-render the Identity + Pay-rate blocks here — the
 * form itself is the identity + pay surface now. Those two panels
 * remain wired in `EmployeeDetail.jsx` (Profile tab only, Iter 357)
 * as a fallback for legacy tenants; they're hidden when the form
 * mode is active. Once every tenant is migrated we can remove them.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import {
  UserRound, Phone, Briefcase, Wallet, Landmark, ShieldCheck,
  HeartPulse, Users, StickyNote, Save, Loader2, AlertCircle,
  ImageIcon, Trash2, Check,
} from "lucide-react";
import StepIndex from "../StepIndex";
import PayRateBlock from "./PayRateBlock";
import IdentityBlock from "./IdentityBlock";
import PhoneInput from "../PhoneInput";
import DatePickerInput from "../DatePickerInput";
import api, { formatErr } from "../../lib/api";
import { toast } from "../ui/sonner";
import DictTextarea from "../DictTextarea";

const GENDERS = ["", "female", "male", "non_binary", "prefer_not_to_say"];
const MARITAL = ["", "single", "married", "cohabiting", "divorced", "widowed"];
const EMPLOYMENT_TYPES = ["", "permanent", "fixed_term", "casual", "intern"];
const STATUSES = ["active", "suspended", "on_leave", "terminated"];
const BLOOD_TYPES = ["", "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "unknown"];
const ACCOUNT_TYPES = ["", "cheque", "savings", "transmission", "business"];

const SECTIONS = [
  { key: "identity",     title: "1 · Identity",              icon: UserRound  },
  { key: "contact",      title: "2 · Contact",               icon: Phone      },
  { key: "employment",   title: "3 · Employment",            icon: Briefcase  },
  { key: "remuneration", title: "4 · Working hours",       icon: Wallet     },
  { key: "banking",      title: "5 · Banking",               icon: Landmark   },
  { key: "statutory",    title: "6 · Statutory",             icon: ShieldCheck},
  { key: "medical",      title: "7 · Medical & Emergency",   icon: HeartPulse },
  { key: "kin",          title: "8 · Next of Kin",           icon: Users      },
  { key: "notes",        title: "9 · HR Notes",              icon: StickyNote },
];

export default function EmployeeProfileForm({ emp, uid, canEdit, onSaved, selfMode = false, isHr = false }) {
  const [active, setActive] = useState("identity");
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef(null);
  const empSnapshot = useMemo(() => normalise(emp), [emp]);
  const [form, setForm] = useState(empSnapshot);
  // Iter 425.g (Dirk, 2026-03-01) — Track the baseline the form was
  // hydrated FROM so save() can send ONLY the fields the user actually
  // touched. Iter 425.i (2026-03-01) — Also stop resetting `form` from
  // the parent's `emp` after the initial hydration. Prior implementation
  // re-normalised on every parent state change, which produced a
  // race: if the parent handed us a fresh `emp` from the save response
  // that omitted (or nulled) any field the user had typed, the form
  // instantly wiped that field. The user's typed values must be sacred
  // once they've been captured — we only re-hydrate on user_id switch.
  const [baseline, setBaseline] = useState(empSnapshot);
  const hydratedUidRef = useRef(null);

  useEffect(() => {
    // Only reset the form on FIRST hydration of a given user. When the
    // parent merges save-response into `emp` we ignore it — the form
    // already reflects the truth (we just typed it) and the baseline
    // is refreshed manually after a successful save (see saveSection).
    if (!emp) return;
    if (hydratedUidRef.current === uid) return;
    hydratedUidRef.current = uid;
    const next = normalise(emp);
    setForm(next);
    setBaseline(next);
  }, [emp, uid]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setKin = (idx, patch) => setForm((f) => {
    const list = [...(f.next_of_kin || [])];
    list[idx] = { ...(list[idx] || {}), ...patch };
    return { ...f, next_of_kin: list };
  });
  const addKin = () => setForm((f) => ({ ...f, next_of_kin: [...(f.next_of_kin || []), {}] }));
  const removeKin = (idx) => setForm((f) => ({ ...f, next_of_kin: (f.next_of_kin || []).filter((_, i) => i !== idx) }));

  const saveSection = async (fields) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      // Iter 425.g (Dirk, 2026-03-01) — Send ONLY the fields the user
      // touched since the form was hydrated. Prior implementation
      // (Iter 419.t) sent the entire form and converted every empty
      // string to null, silently wiping HR fields the user never
      // looked at. We now:
      //   • Diff every top-level key in the form against the baseline.
      //     Nested objects (address, bank_account, emergency_contact,
      //     next_of_kin) are compared as JSON so a nested edit sends
      //     the whole object (backend expects the full sub-doc).
      //   • ALWAYS include the fields the section explicitly listed,
      //     so hitting "Save section" on a fully-empty section still
      //     lets HR clear a stored value (e.g. blanking a preferred
      //     name).
      //   • Convert `""` to `null` at the end for keys included.
      const forceKeys = new Set(Object.keys(fields || {}));
      const patch = { user_id: uid };
      const jsonEq = (a, b) => {
        // Cheap deep-equal for plain values / arrays / dict objects.
        try { return JSON.stringify(a ?? null) === JSON.stringify(b ?? null); }
        catch { return false; }
      };
      for (const k of Object.keys(form)) {
        if (!forceKeys.has(k) && jsonEq(form[k], baseline[k])) continue;
        patch[k] = form[k];
      }
      // Explicit section fields override with the newest form value.
      for (const k of forceKeys) {
        patch[k] = fields[k] !== undefined ? fields[k] : form[k];
      }
      // Empty string → null for the fields we're actually sending.
      Object.keys(patch).forEach((k) => { if (patch[k] === "") patch[k] = null; });
      if (patch.pay_rate !== undefined) {
        patch.pay_rate = patch.pay_rate ? Number(patch.pay_rate) : null;
      }
      const { data } = await api.put(`/team/employees/${uid}`, patch);
      // Iter 425.i — Bring the baseline forward using what we JUST
      // saved (patch), NOT the parent's `emp`. This keeps future
      // diffs correct while guaranteeing we never overwrite the
      // user's typed form values with a stale/partial response.
      setBaseline((prev) => ({ ...prev, ...patch }));
      onSaved?.(data);
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Save failed."));
    } finally { setBusy(false); }
  };

  // ── Avatar upload (Identity step) ─────────────────────────
  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Avatar must be ≤ 10 MB."); return; }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      await api.post(`/users/${uid}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar updated.");
      onSaved?.({ ...emp, avatar_url: null });  // force refetch upstream
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not upload avatar"));
    } finally { setAvatarBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const deleteAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete(`/users/${uid}/avatar`);
      toast.success("Avatar removed.");
      onSaved?.({ ...emp, avatar_url: null });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail, "Could not remove avatar"));
    } finally { setAvatarBusy(false); }
  };

  // ── Completion checks for the StepIndex tick marks ─────────
  const isSectionComplete = (key) => {
    if (key === "identity")     return !!(form.first_name && form.last_name && (form.id_number || form.passport_number));
    if (key === "contact")      return !!(form.cell_no || form.bus_tel_no) && !!form.physical_address?.line1;
    if (key === "employment")   return !!(form.job_title && form.appointment_date && form.employment_type);
    if (key === "remuneration") return !!emp?.pay_rate_amount;
    if (key === "banking")      return !!(form.bank_account?.bank_name && form.bank_account?.account_number);
    if (key === "statutory")    return !!(form.income_tax_number || form.uif_number);
    if (key === "medical")      return !!(form.blood_type || form.emergency_contact?.name);
    if (key === "kin")          return (form.next_of_kin || []).some((k) => k?.name);
    if (key === "notes")        return !!form.notes;
    return false;
  };
  const completedCount = SECTIONS.filter((s) => isSectionComplete(s.key)).length;
  const progressPct = Math.round((completedCount / SECTIONS.length) * 100);

  // ── Section renderers ─────────────────────────────────────
  const readonlyProps = { canEdit, busy };

  return (
    <div className="grid lg:grid-cols-[240px,1fr] gap-4" data-testid="employee-profile-form">
      <aside className="self-start">
        <StepIndex
          sections={SECTIONS.map((s) => ({ key: s.key, title: s.title, icon: s.icon }))}
          activeKey={active}
          onJump={(k) => setActive(k)}
          completionPct={progressPct}
          isComplete={isSectionComplete}
          testId="emp-form-step-index"
        />
      </aside>
      <div className="space-y-4 min-w-0">
        {!canEdit && (
          <div className="text-[11px] text-amber-300 inline-flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" />
            Read-only — only owners and admins can edit HR fields.
          </div>
        )}

        {active === "identity" && (
          <Section title="Identity">
            <div className="flex items-start gap-4 flex-wrap mb-3">
              <div className="relative">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] flex items-center justify-center text-2xl font-bold uppercase text-[#0a0b0e]">
                  {emp.avatar_url
                    ? <img src={emp.avatar_url} alt={emp.name} className="w-full h-full object-cover" />
                    : ((form.first_name || form.last_name || emp.name || emp.email || "?").slice(0, 1))}
                </div>
                {canEdit && (
                  <>
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#1FB6C1] text-[#0a0b0e] flex items-center justify-center hover:scale-110 transition"
                      disabled={avatarBusy}
                      data-testid="emp-avatar-upload"
                    >
                      {avatarBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadAvatar(e.target.files?.[0])} />
                  </>
                )}
              </div>
              {emp.avatar_url && canEdit && (
                <button onClick={deleteAvatar} className="text-[10px] uppercase tracking-wider text-rose-300 hover:text-rose-200 self-center" data-testid="emp-avatar-remove">
                  <Trash2 className="w-2.5 h-2.5 inline mr-0.5" /> Remove avatar
                </button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="First name" req>
                <input className="hv-input" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} disabled={!canEdit} data-testid="ep-first-name" />
              </Field>
              <Field label="Surname" req>
                <input className="hv-input" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} disabled={!canEdit} data-testid="ep-last-name" />
              </Field>
              <Field label="Preferred name">
                <input className="hv-input" value={form.preferred_name} onChange={(e) => set({ preferred_name: e.target.value })} disabled={!canEdit} data-testid="ep-preferred-name" />
              </Field>
              <Field label="Nationality">
                <input className="hv-input" placeholder="e.g. South African" value={form.nationality} onChange={(e) => set({ nationality: e.target.value })} disabled={!canEdit} data-testid="ep-nationality" />
              </Field>
              <Field label="SA ID number">
                <input className="hv-input" value={form.id_number} onChange={(e) => set({ id_number: e.target.value })} disabled={!canEdit} data-testid="ep-id-number" />
              </Field>
              <Field label="Passport (if non-SA)">
                <input className="hv-input" value={form.passport_number} onChange={(e) => set({ passport_number: e.target.value })} disabled={!canEdit} data-testid="ep-passport" />
              </Field>
              <Field label="Gender">
                <select className="hv-input" value={form.gender} onChange={(e) => set({ gender: e.target.value })} disabled={!canEdit} data-testid="ep-gender">
                  {GENDERS.map((g) => <option key={g} value={g}>{g ? g.replace(/_/g, " ") : "—"}</option>)}
                </select>
              </Field>
              <Field label="Marital status">
                <select className="hv-input" value={form.marital_status} onChange={(e) => set({ marital_status: e.target.value })} disabled={!canEdit} data-testid="ep-marital">
                  {MARITAL.map((m) => <option key={m} value={m}>{m || "—"}</option>)}
                </select>
              </Field>
              <Field label="Birthdate">
                <DatePickerInput
                  value={form.birthdate}
                  onChange={(v) => set({ birthdate: v })}
                  disabled={!canEdit}
                  testId="ep-birthdate"
                />
                <p className="text-[10px] text-[#647087] mt-0.5">Auto-filled from SA ID if left blank.</p>
              </Field>
            </div>
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({
                first_name: form.first_name, last_name: form.last_name,
                preferred_name: form.preferred_name, nationality: form.nationality,
                id_number: form.id_number, passport_number: form.passport_number,
                gender: form.gender, marital_status: form.marital_status,
                birthdate: form.birthdate,
              })}
              testId="ep-save-identity"
            />
          </Section>
        )}

        {active === "contact" && (
          <Section title="Contact">
            {/* IdentityBlock lets HR see/manage login + personal emails.
                Read-only cue on the login email — HR is instructed to
                the Users & Roles tool for actual login-email swaps. */}
            {(isHr || selfMode) && <IdentityBlock userId={uid} selfMode={selfMode} canManage={isHr} />}
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <Field label="Cell number">
                <PhoneInput value={form.cell_no || ""} onChange={(v) => set({ cell_no: v })} disabled={!canEdit} testId="ep-cell" />
              </Field>
              <Field label="Business tel">
                <PhoneInput value={form.bus_tel_no || ""} onChange={(v) => set({ bus_tel_no: v })} disabled={!canEdit} testId="ep-bustel" />
              </Field>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mt-4 mb-1">Physical address</div>
            <AddressFields
              value={form.physical_address || {}}
              onChange={(a) => set({ physical_address: a })}
              disabled={!canEdit}
              prefix="ep-phy"
            />
            <div className="flex items-center gap-2 mt-4 mb-1">
              <div className="text-[10px] uppercase tracking-wider text-[#647087]">Postal address</div>
              {canEdit && (
                <button
                  type="button"
                  className="text-[10px] text-[#1FB6C1] hover:underline"
                  onClick={() => set({ postal_address: { ...(form.physical_address || {}) } })}
                  data-testid="ep-postal-same"
                >
                  Same as physical
                </button>
              )}
            </div>
            <AddressFields
              value={form.postal_address || {}}
              onChange={(a) => set({ postal_address: a })}
              disabled={!canEdit}
              prefix="ep-post"
            />
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({
                cell_no: form.cell_no, bus_tel_no: form.bus_tel_no,
                physical_address: form.physical_address, postal_address: form.postal_address,
              })}
              testId="ep-save-contact"
            />
          </Section>
        )}

        {active === "employment" && (
          <Section title="Employment">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Job title" req>
                <input className="hv-input" value={form.job_title} onChange={(e) => set({ job_title: e.target.value })} disabled={!canEdit} data-testid="ep-job-title" />
              </Field>
              <Field label="Employee #">
                <input className="hv-input" value={form.employee_number} onChange={(e) => set({ employee_number: e.target.value })} disabled={!canEdit} data-testid="ep-emp-number" />
              </Field>
              <Field label="Department">
                <input className="hv-input" value={form.department} onChange={(e) => set({ department: e.target.value })} disabled={!canEdit} data-testid="ep-dept" />
              </Field>
              <Field label="Job grade">
                <input className="hv-input" value={form.job_grade} onChange={(e) => set({ job_grade: e.target.value })} disabled={!canEdit} data-testid="ep-grade" />
              </Field>
              <Field label="Employment type">
                <select className="hv-input" value={form.employment_type} onChange={(e) => set({ employment_type: e.target.value })} disabled={!canEdit} data-testid="ep-emp-type">
                  {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t ? t.replace(/_/g, " ") : "—"}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className="hv-input" value={form.status || (form.is_active === false ? "terminated" : "active")}
                  onChange={(e) => set({ status: e.target.value, is_active: e.target.value !== "terminated" })}
                  disabled={!canEdit} data-testid="ep-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Appointment date">
                <DatePickerInput value={form.appointment_date} onChange={(v) => set({ appointment_date: v })} disabled={!canEdit} testId="ep-appt" />
              </Field>
              <Field label="Termination date">
                <DatePickerInput value={form.termination_date} onChange={(v) => set({ termination_date: v })} disabled={!canEdit} testId="ep-term" />
              </Field>
            </div>
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({
                job_title: form.job_title, employee_number: form.employee_number,
                department: form.department, job_grade: form.job_grade,
                employment_type: form.employment_type, status: form.status,
                is_active: form.status ? form.status !== "terminated" : form.is_active,
                appointment_date: form.appointment_date, termination_date: form.termination_date,
              })}
              testId="ep-save-employment"
            />
          </Section>
        )}

        {active === "remuneration" && (
          <Section title="Working hours">
            <p className="text-[11px] text-[#a0aabf] mb-2">
              Base pay rate, overtime multipliers, per-weekday schedule +
              lunch break. This is the single source of truth the
              attendance register uses to split ordinary vs OT (Iter 362).
            </p>
            <PayRateBlock userId={uid} />
          </Section>
        )}

        {active === "banking" && (
          <Section title="Banking">
            <p className="text-[11px] text-[#a0aabf] mb-2">
              Used to pay salary. Encrypted at rest; only owners and admins
              can see or edit these fields.
            </p>
            <BankFields
              value={form.bank_account || {}}
              onChange={(b) => set({ bank_account: b })}
              disabled={!canEdit}
            />
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({ bank_account: form.bank_account })}
              testId="ep-save-banking"
            />
          </Section>
        )}

        {active === "statutory" && (
          <Section title="Statutory">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="SA income tax number">
                <input className="hv-input" value={form.income_tax_number} onChange={(e) => set({ income_tax_number: e.target.value })} disabled={!canEdit} data-testid="ep-tax" />
              </Field>
              <Field label="UIF number">
                <input className="hv-input" value={form.uif_number} onChange={(e) => set({ uif_number: e.target.value })} disabled={!canEdit} data-testid="ep-uif" />
              </Field>
              <Field label="Medical aid scheme">
                <input className="hv-input" value={form.medical_aid_scheme} onChange={(e) => set({ medical_aid_scheme: e.target.value })} disabled={!canEdit} data-testid="ep-med-scheme" />
              </Field>
              <Field label="Medical aid number">
                <input className="hv-input" value={form.medical_aid_number} onChange={(e) => set({ medical_aid_number: e.target.value })} disabled={!canEdit} data-testid="ep-med-number" />
              </Field>
              <Field label="Medical aid plan">
                <input className="hv-input" value={form.medical_aid_plan} onChange={(e) => set({ medical_aid_plan: e.target.value })} disabled={!canEdit} data-testid="ep-med-plan" />
              </Field>
              <Field label="Dependants covered">
                <input type="number" className="hv-input" value={form.medical_aid_dependants ?? ""} onChange={(e) => set({ medical_aid_dependants: e.target.value === "" ? null : Number(e.target.value) })} disabled={!canEdit} data-testid="ep-med-deps" />
              </Field>
            </div>
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({
                income_tax_number: form.income_tax_number, uif_number: form.uif_number,
                medical_aid_scheme: form.medical_aid_scheme, medical_aid_number: form.medical_aid_number,
                medical_aid_plan: form.medical_aid_plan, medical_aid_dependants: form.medical_aid_dependants,
              })}
              testId="ep-save-statutory"
            />
          </Section>
        )}

        {active === "medical" && (
          <Section title="Medical & Emergency">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Blood type">
                <select className="hv-input" value={form.blood_type} onChange={(e) => set({ blood_type: e.target.value })} disabled={!canEdit} data-testid="ep-blood">
                  {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b || "—"}</option>)}
                </select>
              </Field>
              <Field label="GP name">
                <input className="hv-input" value={form.gp_name} onChange={(e) => set({ gp_name: e.target.value })} disabled={!canEdit} data-testid="ep-gp-name" />
              </Field>
              <Field label="GP phone">
                <PhoneInput value={form.gp_phone || ""} onChange={(v) => set({ gp_phone: v })} disabled={!canEdit} testId="ep-gp-phone" />
              </Field>
              <div />
              <Field label="Allergies" className="sm:col-span-2">
                <DictTextarea className="hv-input" rows={2} value={form.allergies} onChange={(e) => set({ allergies: e.target.value })} disabled={!canEdit} data-testid="ep-allergies" />
              </Field>
              <Field label="Chronic conditions" className="sm:col-span-2">
                <DictTextarea className="hv-input" rows={2} value={form.chronic_conditions} onChange={(e) => set({ chronic_conditions: e.target.value })} disabled={!canEdit} data-testid="ep-chronic" />
              </Field>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mt-4 mb-1">Primary emergency contact</div>
            <EmergencyFields
              value={form.emergency_contact || {}}
              onChange={(c) => set({ emergency_contact: c })}
              disabled={!canEdit}
            />
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({
                blood_type: form.blood_type, gp_name: form.gp_name, gp_phone: form.gp_phone,
                allergies: form.allergies, chronic_conditions: form.chronic_conditions,
                emergency_contact: form.emergency_contact,
              })}
              testId="ep-save-medical"
            />
          </Section>
        )}

        {active === "kin" && (
          <Section title="Next of Kin">
            <p className="text-[11px] text-[#a0aabf] mb-2">
              Add as many next-of-kin as you need. The first entry is used
              as the fallback emergency contact if the Medical section is
              blank.
            </p>
            {(form.next_of_kin || []).length === 0 && (
              <div className="text-xs text-[#647087] italic mb-2">None captured yet.</div>
            )}
            <div className="space-y-3">
              {(form.next_of_kin || []).map((k, idx) => (
                <div key={idx} className="hv-card p-3 space-y-2" data-testid={`ep-kin-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-[#a0e6ea]">Contact #{idx + 1}</div>
                    {canEdit && (
                      <button type="button" onClick={() => removeKin(idx)} className="text-[10px] text-rose-300 hover:text-rose-200" data-testid={`ep-kin-remove-${idx}`}>
                        <Trash2 className="w-2.5 h-2.5 inline" /> Remove
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Field label="Full name">
                      <input className="hv-input" value={k.name || ""} onChange={(e) => setKin(idx, { name: e.target.value })} disabled={!canEdit} />
                    </Field>
                    <Field label="Relationship">
                      <input className="hv-input" placeholder="e.g. spouse, mother" value={k.relationship || ""} onChange={(e) => setKin(idx, { relationship: e.target.value })} disabled={!canEdit} />
                    </Field>
                    <Field label="Cell number">
                      <PhoneInput value={k.cell_no || ""} onChange={(v) => setKin(idx, { cell_no: v })} disabled={!canEdit} />
                    </Field>
                    <Field label="Email">
                      <input type="email" className="hv-input" value={k.email || ""} onChange={(e) => setKin(idx, { email: e.target.value })} disabled={!canEdit} />
                    </Field>
                    <Field label="Address" className="sm:col-span-2">
                      <input className="hv-input" value={k.address || ""} onChange={(e) => setKin(idx, { address: e.target.value })} disabled={!canEdit} />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
            {canEdit && (
              <button type="button" onClick={addKin} className="hv-btn hv-btn-ghost text-xs mt-3" data-testid="ep-kin-add">
                + Add next of kin
              </button>
            )}
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({ next_of_kin: form.next_of_kin })}
              testId="ep-save-kin"
            />
          </Section>
        )}

        {active === "notes" && (
          <Section title="HR Notes">
            <p className="text-[11px] text-[#a0aabf] mb-2">
              Internal HR notes. Never visible to the employee.
            </p>
            <DictTextarea className="hv-input" rows={6} value={form.notes} onChange={(e) => set({ notes: e.target.value })} disabled={!canEdit} data-testid="ep-notes" />
            <SectionActions
              {...readonlyProps}
              onSave={() => saveSection({ notes: form.notes })}
              testId="ep-save-notes"
            />
          </Section>
        )}
      </div>
    </div>
  );
}

/* ── Helper subcomponents ────────────────────────────────── */

function Section({ title, children }) {
  return (
    <div className="hv-card p-4 space-y-2" data-testid={`ep-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="text-sm font-bold text-white">{title}</h2>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, children, req, error, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#647087]">
        {label}{req && <span className="text-rose-300"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
      {error && <div className="text-[10px] text-rose-300 mt-0.5">{error}</div>}
    </label>
  );
}

function SectionActions({ canEdit, busy, onSave, testId }) {
  if (!canEdit) return null;
  return (
    <div className="flex justify-end mt-3">
      <button
        type="button"
        onClick={onSave}
        disabled={busy}
        className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
        data-testid={testId}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Save section
      </button>
    </div>
  );
}

function AddressFields({ value, onChange, disabled, prefix }) {
  const set = (patch) => onChange({ ...(value || {}), ...patch });
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <Field label="Line 1" className="sm:col-span-2">
        <input className="hv-input" value={value.line1 || ""} onChange={(e) => set({ line1: e.target.value })} disabled={disabled} data-testid={`${prefix}-line1`} />
      </Field>
      <Field label="Line 2" className="sm:col-span-2">
        <input className="hv-input" value={value.line2 || ""} onChange={(e) => set({ line2: e.target.value })} disabled={disabled} data-testid={`${prefix}-line2`} />
      </Field>
      <Field label="Suburb">
        <input className="hv-input" value={value.suburb || ""} onChange={(e) => set({ suburb: e.target.value })} disabled={disabled} data-testid={`${prefix}-suburb`} />
      </Field>
      <Field label="City">
        <input className="hv-input" value={value.city || ""} onChange={(e) => set({ city: e.target.value })} disabled={disabled} data-testid={`${prefix}-city`} />
      </Field>
      <Field label="Province">
        <input className="hv-input" value={value.province || ""} onChange={(e) => set({ province: e.target.value })} disabled={disabled} data-testid={`${prefix}-province`} />
      </Field>
      <Field label="Postal code">
        <input className="hv-input" value={value.postal_code || ""} onChange={(e) => set({ postal_code: e.target.value })} disabled={disabled} data-testid={`${prefix}-postal`} />
      </Field>
      <Field label="Country">
        <input className="hv-input" placeholder="South Africa" value={value.country || ""} onChange={(e) => set({ country: e.target.value })} disabled={disabled} data-testid={`${prefix}-country`} />
      </Field>
    </div>
  );
}

function BankFields({ value, onChange, disabled }) {
  const set = (patch) => onChange({ ...(value || {}), ...patch });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Account holder name">
        <input className="hv-input" value={value.account_holder || ""} onChange={(e) => set({ account_holder: e.target.value })} disabled={disabled} data-testid="ep-bank-holder" />
      </Field>
      <Field label="Bank name">
        <input className="hv-input" placeholder="e.g. FNB, Nedbank, ABSA, Standard" value={value.bank_name || ""} onChange={(e) => set({ bank_name: e.target.value })} disabled={disabled} data-testid="ep-bank-name" />
      </Field>
      <Field label="Account number">
        <input className="hv-input" value={value.account_number || ""} onChange={(e) => set({ account_number: e.target.value })} disabled={disabled} data-testid="ep-bank-number" />
      </Field>
      <Field label="Branch code">
        <input className="hv-input" value={value.branch_code || ""} onChange={(e) => set({ branch_code: e.target.value })} disabled={disabled} data-testid="ep-bank-branch" />
      </Field>
      <Field label="Account type">
        <select className="hv-input" value={value.account_type || ""} onChange={(e) => set({ account_type: e.target.value })} disabled={disabled} data-testid="ep-bank-type">
          {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t || "—"}</option>)}
        </select>
      </Field>
    </div>
  );
}

function EmergencyFields({ value, onChange, disabled }) {
  const set = (patch) => onChange({ ...(value || {}), ...patch });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Name">
        <input className="hv-input" value={value.name || ""} onChange={(e) => set({ name: e.target.value })} disabled={disabled} data-testid="ep-em-name" />
      </Field>
      <Field label="Relationship">
        <input className="hv-input" value={value.relationship || ""} onChange={(e) => set({ relationship: e.target.value })} disabled={disabled} data-testid="ep-em-rel" />
      </Field>
      <Field label="Cell">
        <PhoneInput value={value.cell_no || ""} onChange={(v) => set({ cell_no: v })} disabled={disabled} testId="ep-em-cell" />
      </Field>
      <Field label="Email">
        <input type="email" className="hv-input" value={value.email || ""} onChange={(e) => set({ email: e.target.value })} disabled={disabled} data-testid="ep-em-email" />
      </Field>
    </div>
  );
}

/* ── Data normaliser ─────────────────────────────────────── */

function normalise(emp) {
  if (!emp) return {};
  // Backfill first/last from `name` when the record hasn't been
  // split yet — HR just landed on the form for the first time.
  let fn = emp.first_name || "";
  let ln = emp.last_name || "";
  if ((!fn || !ln) && emp.name) {
    const parts = emp.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      if (!fn) fn = parts[0];
      if (!ln) ln = parts.slice(1).join(" ");
    } else if (parts[0] && !fn) {
      fn = parts[0];
    }
  }
  return {
    first_name: fn || "",
    last_name: ln || "",
    preferred_name: emp.preferred_name || "",
    gender: emp.gender || "",
    marital_status: emp.marital_status || "",
    nationality: emp.nationality || "",
    id_number: emp.id_number || "",
    passport_number: emp.passport_number || "",
    birthdate: emp.birthdate || "",
    cell_no: emp.cell_no || "",
    bus_tel_no: emp.bus_tel_no || "",
    physical_address: emp.physical_address || {},
    postal_address: emp.postal_address || {},
    employee_number: emp.employee_number || "",
    job_title: emp.job_title || "",
    job_grade: emp.job_grade || "",
    department: emp.department || "",
    employment_type: emp.employment_type || "",
    status: emp.status || (emp.is_active === false ? "terminated" : "active"),
    appointment_date: emp.appointment_date || "",
    termination_date: emp.termination_date || "",
    is_active: emp.is_active !== false,
    bank_account: emp.bank_account || {},
    income_tax_number: emp.income_tax_number || "",
    uif_number: emp.uif_number || "",
    medical_aid_scheme: emp.medical_aid_scheme || "",
    medical_aid_number: emp.medical_aid_number || "",
    medical_aid_plan: emp.medical_aid_plan || "",
    medical_aid_dependants: emp.medical_aid_dependants ?? "",
    blood_type: emp.blood_type || "",
    allergies: emp.allergies || "",
    chronic_conditions: emp.chronic_conditions || "",
    gp_name: emp.gp_name || "",
    gp_phone: emp.gp_phone || "",
    emergency_contact: emp.emergency_contact || {},
    next_of_kin: emp.next_of_kin || [],
    notes: emp.notes || "",
  };
}
